import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Autocomplete,
  MenuItem,
  useTheme,
  Collapse,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';

import { phoneCountries } from './countries';
import lukyMaintenance from '../assets/luky-maintenance.png';
import lukyInactive from '../assets/luky-inactive.png';
import Icons from '../components/icons';
import { validateUser } from '../services/auth.service';
import { hasDarkModeLogo } from '../utils/configHelpers';
import { useBrandConfig } from '../stores/brandConfigContext';
import { useLoginSessionStore } from '../stores/loginSessionStore';


interface CountryOption {
  value: string; // Código internacional, ej: '+58'
  label: string; // ISO, ej: 'VE'
  name: string; // Nombre país, ej: 'Venezuela'
}

interface StepEmailProps {
  onNext: (data: { email?: string; phone?: string }) => void;
}

const defaultCountry: CountryOption = { value: '+58', label: 'VE', name: 'Venezuela' };

const StepEmail: React.FC<StepEmailProps> = ({ onNext }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { config: brandConfig } = useBrandConfig();
  const [loginType, setLoginType] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<CountryOption>(defaultCountry);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [shake, setShake] = useState(false);
  const [countryDetected, setCountryDetected] = useState(false);
  
  // Detectar estados del portal desde configuración
  const isUnderMaintenance = brandConfig?.mantenimiento === true;
  const isPortalInactive = brandConfig?.portalPagos === false;

  // Detectar país automáticamente basado en IP del browser
  useEffect(() => {
    if (countryDetected) return;
    
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/', { 
          signal: AbortSignal.timeout(5000) 
        });
        const data = await response.json();
        const countryCode = data.country_code; // Ej: "VE", "US", "CO"
        
        // Buscar el país en la lista
        const detected = phoneCountries.find(c => c.label === countryCode);
        if (detected) {
          setCountry({
            value: detected.value,
            label: detected.label,
            name: detected.label // Usar label como name ya que countries.ts no tiene name
          });
          console.log('[StepEmail] Country detected:', countryCode);
        }
        setCountryDetected(true);
      } catch (error) {
        console.log('[StepEmail] Could not detect country, using default');
        setCountryDetected(true);
      }
    };
    
    detectCountry();
  }, [countryDetected]);

  // Obtener configuración de marca si está disponible
  const config = (window as any).PAGOS_CONFIG;
  const esPagosConMarca = config?.esPagosConMarca;
  const mostrarLogoMarca = config?.mostrarLogoMarca;

  // Actualizar título dinámicamente
  useEffect(() => {
    if (brandConfig?.nombre) {
      document.title = `Portal de Pagos ${brandConfig.nombre}`;
    } else {
      document.title = 'Portal de Pagos';
    }
  }, [brandConfig]);

  // Obtener FAQs desde brandConfig (reactivo al API response)
  const configFAQs = useMemo(() => {
    try {
      const faqString = brandConfig?.faq;
      if (faqString && typeof faqString === 'string') {
        return JSON.parse(faqString);
      }
      return [];
    } catch (error) {
      console.error('Error parsing FAQ JSON:', error);
      return [];
    }
  }, [brandConfig?.faq]);
  
  // Preguntas frecuentes con prioridad a configuración del API
  const faqData = useMemo(() => {
    // Si hay FAQs desde el API, usarlas
    if (configFAQs.length > 0) {
      return configFAQs.map((faq: any) => ({
        question: faq.question,
        answer: faq.answer,
        isStructured: false
      }));
    }
    
    // Fallback a traducciones locales
    return [
      {
        question: t('faq.question1'),
        answer: t('faq.answer1'),
        isStructured: true,
        structure: {
          sections: [
            {
              title: t('faq.answer1.paymentMethods'),
              items: t('faq.answer1.items.paymentMethods', { returnObjects: true }) as string[]
            },
            {
              title: t('faq.answer1.banks'),
              items: t('faq.answer1.items.banks', { returnObjects: true }) as string[]
            }
          ]
        }
      },
      {
        question: t('faq.question2'),
        answer: t('faq.answer2'),
        isStructured: false
      },
      {
        question: t('faq.question3'),
        answer: t('faq.answer3'),
        isStructured: false
      },
      {
        question: t('faq.question4'),
        answer: t('faq.answer4'),
        isStructured: false
      }
    ];
  }, [configFAQs, t]);

  const handleFAQHover = (index: number) => {
    setOpenFAQ(index);
  };

  const handleFAQLeave = () => {
    setOpenFAQ(null);
  };

  // Función para formatear respuestas con bullets
  const formatAnswer = (faq: any) => {
    if (faq.isStructured && faq.structure) {
      return (
        <Box>
          {faq.structure.sections.map((section: any, sectionIndex: number) => (
            <Box key={sectionIndex} sx={{ mb: sectionIndex === faq.structure.sections.length - 1 ? 0 : 1 }}>
              <Typography 
                sx={{ 
                  fontSize: '13px', 
                  color: 'text.primary',
                  lineHeight: 1.4,
                  fontWeight: 600,
                  mb: 0.3
                }}
              >
                {section.title}
              </Typography>
              
              {(section.title.toLowerCase().includes('banco') || 
                section.title.toLowerCase().includes('bank') || 
                section.title.toLowerCase().includes('método') || 
                section.title.toLowerCase().includes('method')) ? (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 0.2,
                  columnGap: 1
                }}>
                  {section.items.map((item: string, itemIndex: number) => (
                    <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box 
                        sx={{ 
                          width: '3px', 
                          height: '3px', 
                          borderRadius: '50%', 
                          backgroundColor: theme.palette.primary.main,
                          mt: 0.6,
                          mr: 0.7,
                          flexShrink: 0
                        }} 
                      />
                      <Typography 
                        sx={{ 
                          fontSize: '13px', 
                          color: 'text.primary',
                          lineHeight: 1.3
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box>
                  {section.items.map((item: string, itemIndex: number) => (
                    <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.2 }}>
                      <Box 
                        sx={{ 
                          width: '3px', 
                          height: '3px', 
                          borderRadius: '50%', 
                          backgroundColor: theme.palette.primary.main,
                          mt: 0.6,
                          mr: 0.7,
                          flexShrink: 0
                        }} 
                      />
                      <Typography 
                        sx={{ 
                          fontSize: '13px', 
                          color: 'text.primary',
                          lineHeight: 1.3
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      );
    } else {
      return (
        <Typography 
          sx={{ 
            fontSize: '13px', 
            color: 'text.primary',
            lineHeight: 1.4,
            whiteSpace: 'pre-line'
          }}
        >
          {faq.answer}
        </Typography>
      );
    }
  };


  // --- Validaciones ---
  const validateEmailFormat = (emailValue: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };
  
  // Alias para mantener compatibilidad
  const validateEmail = validateEmailFormat;
  

  const validatePhone = (phone: string) => {
    const fullNumber = `${country.value}${phone.replace(/^0+/, '')}`;
    return isValidPhoneNumber(fullNumber, country.label as any);
  };

  // --- Formateo en vivo ---
  const formatPhone = (value: string) => {
    try {
      return new AsYouType(country.label as any).input(value);
    } catch {
      return value;
    }
  };

  // Variantes de animación shake estilo Apple
  const shakeVariants = {
    shake: {
      x: [0, -10, 10, -10, 10, -5, 5, -2, 2, 0],
      transition: { duration: 0.5, ease: "easeInOut" }
    },
    idle: {
      x: 0
    }
  };

  // --- Submit ---
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setShake(false);

    if (loginType === 'email') {
      if (!email) {
        setError(t('login.emailRequired'));
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
      if (!validateEmail(email)) {
        setError(t('login.emailInvalid'));
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
    } else {
      if (!phone) {
        setError(t('login.phoneRequired'));
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
      if (!validatePhone(phone)) {
        setError(t('login.phoneInvalid'));
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
    }

    setLoading(true);
    try {
      const { startSession } = useLoginSessionStore.getState();
      
      if (loginType === 'email') {
        const configMarca = brandConfig;
        const idMarcaCfg = Number(configMarca?.idMarca ?? 0);
        
        // Llamar al servicio de validación
        await validateUser({ Email: email, IdMarca: idMarcaCfg });
        
        // Iniciar timer de sesión de login (5 minutos)
        startSession(email);
        
        // Si éxito (200), navegar a OTP
        onNext({ email });
      } else {
        const configMarca = brandConfig;
        const idMarcaCfg = Number(configMarca?.idMarca ?? 0);
        
        // Formatear teléfono: código país sin + y número sin ceros iniciales ni espacios
        // Ej: +58 412 025 5587 -> 584120255587
        const cleanPhone = phone.replace(/^0+/, '').replace(/\s/g, '');
        const fullNumber = `${country.value.replace('+', '')}${cleanPhone}`;
        
        // Llamar al servicio de validación con Telefono
        await validateUser({ Telefono: fullNumber, IdMarca: idMarcaCfg });
        
        // Iniciar timer de sesión de login (5 minutos)
        startSession(fullNumber);
        
        // Si éxito, navegar a OTP
        onNext({ phone: fullNumber });
      }
    } catch (err: any) {
      const errorKey = err?.message || 'serverError';
      
      // Si el mensaje es una key de traducción, traducir
      const translatedError = t(`login.${errorKey}`, { defaultValue: '' });
      
      // Si no hay traducción, usar el mensaje original o un fallback
      setError(translatedError || err?.message || t('login.serverError'));
      
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  // Handler para tecla Enter
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      const isValid = loginType === 'email' 
        ? email && validateEmail(email)
        : phone && validatePhone(phone);
      if (isValid) {
        handleSubmit();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [email, phone, loginType, loading]);

  // --- Render ---
  return (
    <motion.div
      style={{ 
        width: '100%', 
        maxWidth: 520, 
        margin: '0 auto'
      }}
    >
      <Box sx={{ p: 0 }}>
        <Stack spacing={0} >
          {/* Logo de la marca si está disponible */}
          {esPagosConMarca && mostrarLogoMarca && brandConfig?.logo && (
            <Box 
              className="marca-logo-container"
              sx={{ 
                textAlign: 'center',
                p: 0,
                mb: 0,
                mt: 0
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', minHeight: '80px', alignItems: 'center' }}>
                {/* Logo light - siempre cargado */}
                <img
                  src={brandConfig.logo}
                  alt={brandConfig.nombre || 'Logo de la empresa'}
                  className="marca-logo"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '80px',
                    objectFit: 'contain',
                    opacity: theme.palette.mode === 'light' || !hasDarkModeLogo() || !brandConfig.logoDark ? 1 : 0,
                    transition: 'opacity 0.15s ease-in-out',
                    position: hasDarkModeLogo() && brandConfig.logoDark ? 'absolute' : 'relative'
                  }}
                />
                {/* Logo dark - solo si existe */}
                {hasDarkModeLogo() && brandConfig.logoDark && (
                  <img
                    src={brandConfig.logoDark}
                    alt={brandConfig.nombre || 'Logo de la empresa'}
                    className="marca-logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      opacity: theme.palette.mode === 'dark' ? 1 : 0,
                      transition: 'opacity 0.15s ease-in-out'
                    }}
                  />
                )}
              </Box>
              <Typography  sx={{ fontSize: '24px', fontWeight: 400, color: 'text.primary', textAlign: 'center', mt: 3 }}>
                {t('login.name')}
              </Typography>
              {brandConfig?.nombre && (
                <Typography 
                  className="marca-nombre"
                  sx={{
                    fontSize: '24px',
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center'
                  }}
                >
                  {brandConfig.nombre}
                </Typography>
              )}

            </Box>
          )}

          {/* CONTENIDO CONDICIONAL: Mantenimiento vs Portal Inactivo vs Login Normal */}
          {isPortalInactive ? (
            /* ========== PORTAL INACTIVO (PRELACIÓN SOBRE MANTENIMIENTO) ========== */
            <Box sx={{ mt: 3 }}>
              <Stack 
                direction="column" 
                alignItems="center" 
                spacing={2}
                sx={{ mb: 3 }}
              >
                {/* Imagen del robot con piso colorido - igual que mantenimiento */}
                <Box 
                  sx={{ 
                    position: 'relative', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center' 
                  }}
                >
                  {/* Imagen del robot inactivo */}
                  <Box
                    component="img"
                    src={lukyInactive}
                    alt="Portal inactivo"
                    sx={{
                      width: { xs: 180, sm: 220 },
                      height: 'auto',
                    }}
                  />
                </Box>
                
                {/* Título en negritas */}
                <Typography 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '17px', 
                    lineHeight: '26px',
                    color: 'text.primary',
                    textAlign: 'center',
                  }}
                >
                  {t('portalInactive.title')}
                </Typography>
              </Stack>
              
              <Typography 
                sx={{ 
                  fontWeight: 400,
                  fontSize: '17px', 
                  lineHeight: '26px',
                  color: 'text.secondary',
                  textAlign: 'center'
                }}
              >
                {t('portalInactive.description')} <strong>{brandConfig?.nombre}</strong>
              </Typography>
            </Box>
          ) : isUnderMaintenance ? (
            /* ========== MANTENIMIENTO CONTROLADO ========== */
            <Box sx={{ mt: 3 }}>
              <Stack 
                direction="column" 
                alignItems="center" 
                spacing={2}
                sx={{ mb: 3 }}
              >
                {/* Imagen del robot de mantenimiento con piso colorido */}
                <Box 
                  sx={{ 
                    position: 'relative', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center' 
                  }}
                >
                  {/* Imagen del robot */}
                  <Box
                    component="img"
                    src={lukyMaintenance}
                    alt="Luky en mantenimiento"
                    sx={{
                      width: { xs: 180, sm: 220 },
                      height: 'auto',
                    }}
                  />
                </Box>
                
                {/* Título en negritas */}
                <Typography 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '17px', 
                    lineHeight: '26px',
                    color: 'text.primary',
                    textAlign: 'center',
                  }}
                >
                  {t('portalMaintenance.title')}
                </Typography>
              </Stack>
              
              <Typography 
                sx={{ 
                  fontWeight: 400,
                  fontSize: '17px', 
                  lineHeight: '26px',
                  color: 'text.secondary',
                  textAlign: 'center'
                }}
              >
                {t('portalMaintenance.description')}
              </Typography>
            </Box>
          ) : (
            /* ========== LOGIN NORMAL ========== */
            <>
              {/* Title */}
              <Stack spacing={0} textAlign="left" sx={{ mt: 2 }}>
                <Typography 
                  sx={{ 
                    fontWeight: 400,
                    fontSize: '14px', 
                    lineHeight: '23px',
                  }}
                >
                  {t('login.title')} <br />
                  {t('login.subtitle')} {brandConfig?.nombre}
                </Typography>
              </Stack>

             {/* Tabs para seleccionar tipo de login */}
             <Stack direction="row" spacing={2} mt={3} mb={2}>
            <Button
              variant="text"
              disableTouchRipple
              disableFocusRipple
              onClick={() => setLoginType('email')}
              sx={{
                fontWeight: loginType === 'email' ? 600 : 400,
                color: loginType === 'email' ? 'primary.main' : 'text.secondary',
                // borderBottom: loginType === 'email' ? '2px solid' : 'none',
                // borderColor: loginType === 'email' ? 'primary.main' : 'transparent',
                border: 'none',
                boxShadow: 'none',
                background: 'none',
                height: '20px',
                minWidth: 0,
                px: 0,
                fontSize: '12px',
                textTransform: 'none',
                '&:hover': {
                  background: 'none',
                  border: 'none',
                  // borderBottom: '2px solid',
                  // borderColor: 'primary.main',
                }
              }}
            >
              Email
            </Button>
            <Button
              disableTouchRipple
              disableFocusRipple
              variant="text"
              onClick={() => setLoginType('phone')}
              sx={{
                fontWeight: loginType === 'phone' ? 600 : 400,
                color: loginType === 'phone' ? 'primary.main' : 'text.secondary',
                // borderBottom: loginType === 'phone' ? '2px solid' : 'none',
                // borderColor: loginType === 'phone' ? 'error.main' : 'transparent',
                border: 'none',
                boxShadow: 'none',
                height: '20px',
                background: 'none',
                minWidth: 0,
                px: 0,
                fontSize: '12px',
                textTransform: 'none',
                '&:hover': {
                  background: 'none',
                  border: 'none',
                  // borderBottom: '2px solid',
                  // borderColor: 'error.main',
                }
              }}
            >
              {t('login.phoneLabel')}
            </Button>
          </Stack>

          {/* Form con animación shake */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <motion.div
              variants={shakeVariants}
              animate={shake ? "shake" : "idle"}
            >
              <Stack spacing={3}>
                {loginType === 'email' ? (
                  <TextField
                    fullWidth
                    label={t('login.emailLabel')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="standard"
                    disabled={loading}
                  />
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <Autocomplete
                      disablePortal
                      disableClearable
                      id="country-select"
                      options={phoneCountries}
                      value={country}
                      onChange={(_, newValue) => newValue && setCountry(newValue as CountryOption)}
                      getOptionLabel={(option) => `${option.value}`}
                      isOptionEqualToValue={(option, value) => option.label === value.label}
                      sx={{ width: 110 }}
                      renderOption={(props, option) => (
                        <MenuItem
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                          value={option.value}
                          {...props}
                          key={option.label}
                        >
                          <Icons
                            className={`fi fi-${option.label.toLowerCase()}`}
                            style={{
                              width: '20px',
                              height: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              marginRight: '8px',
                              borderRadius: '4px',
                            }}
                          />
                        <Typography variant="caption" color={theme.palette.text.medium}>
                          {option.value}
                        </Typography>
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label={t('login.phoneCountryLabel') || 'País'}
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <Icons
                              className={`fi fi-${country.label.toLowerCase()}`}
                              style={{
                                maxWidth: '25px',
                                maxHeight: '18px',
                                width: '25px',
                                height: '18px',
                                objectFit: 'cover',
                                marginBottom: '5px',
                                borderRadius: '4px',
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    label={t('login.phoneLabel') || 'Número celular'}
                    variant="standard"
                    value={formatPhone(phone)}
                    onChange={(e) => {
                      // Solo números
                      const raw = e.target.value.replace(/\D/g, '');
                      setPhone(raw);
                    }}
                    disabled={loading}
                    inputProps={{ maxLength: 15 }}
                  />
                </Stack>
              )}
               <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={ loading || (loginType === 'email' ? !validateEmail(email) : !validatePhone(phone)) }
                sx={{ 
                  py: 2,
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '50px',
                  '&:disabled': {
                    opacity: 0.5,
                    backgroundColor: 'primary.main',
                    color: theme.palette.text.white,
                  }
                }}
              >
                {loading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={26} thickness={5} color="inherit" />
                  </Stack>
                ) : (
                  t('login.sendCode')
                )}
              </Button>
            </Stack>

            {/* Error en texto rojo debajo del botón */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                >
              <Typography
                sx={{
                  color: '#FF3B30',
                  fontSize: '13px',
                  fontWeight: 500,
                  mt: 1.5,
                  textAlign: 'center'
                }}
              >
                {error}
              </Typography>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          </Box>

          {/* FAQ Section */}
          <Box sx={{ mt: 2 }}>
            <Stack spacing={0.5}>
              {faqData.map((faq: any, index: number) => (
                <Box
                  key={index}
                  onMouseEnter={() => handleFAQHover(index)}
                  onMouseLeave={handleFAQLeave}
                  sx={{
                    borderRadius: '6px',
                    overflow: 'visible',
                    transition: 'background-color 0.2s ease-in-out',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: `${theme.palette.primary.main}05`,
                    }
                  }}
                >
                  <Box sx={{ py: 1, px: 0.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography 
                        sx={{ 
                          fontSize: '14px', 
                          fontWeight: 500,
                          color: openFAQ === index ? 'primary.main' : 'text.primary',
                          transition: 'color 0.2s ease-in-out'
                        }}
                      >
                        {faq.question}
                      </Typography>
                      <motion.div
                        animate={{ rotate: openFAQ === index ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Icons 
                          className="icons-arrow-circle-right" 
                          style={{ 
                            fontSize: '14px',
                            color: openFAQ === index ? theme.palette.primary.main : theme.palette.text.secondary,
                            transform: 'rotate(-90deg)',
                            transition: 'color 0.2s ease-in-out'
                          }}
                        />
                      </motion.div>
                    </Stack>
                    
                    <Collapse 
                      in={openFAQ === index} 
                      timeout={200}
                      sx={{
                        transformOrigin: 'top',
                      }}
                    >
                      <Box sx={{ pt: 1, mt: 0.5, pb: 0.5 }}>
                        {formatAnswer(faq)}
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </>
          )}
        </Stack>
      </Box>
    </motion.div>
  );
};

export default StepEmail;